import { EmptyState, Loading } from "../app/Status";
import { useQuery } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router";
import {
  CERT_STATE_LABELS,
  COMPANY_KIND_LABELS,
  addDays,
  todayIso,
  type CertState,
  type CompanyDto,
  type CompanyKind,
  type CompanyStatsDto,
  type ContactLinkDto,
  type ContractSummaryDto,
  type DirectoryPersonDto,
} from "@klantpunt/shared";
import { Tabs } from "../app/Choice";
import {
  IconAlert,
  IconClose,
  IconContract,
  IconDownload,
  IconMail,
  IconPencil,
  IconPlus,
  IconSearch,
  IconShield,
} from "../app/icons";
import { PageHeader } from "../app/PageHeader";
import { CompanyDialog, errMsg, PersonDialog, useDirectoryRefresh } from "../features/directory/DirectoryDialogs";
import { companyPath, KindChips } from "../features/directory/CompanyPage";
import { LinksSection } from "../features/directory/ContactLinks";
import { LegacyNames, useLegacyNames } from "../features/directory/LegacyNames";
import { MailDialog, type MailTarget } from "../features/directory/Mail";
import { useContracts } from "../features/contracts/useContracts";
import { Logbook } from "../features/logbook/Logbook";
import { Phone } from "../features/directory/parts";
import { api } from "../lib/api";
import { hasModule, useMe } from "../lib/auth";
import { useToast } from "../lib/toast";

type Tab = "bedrijven" | "personen" | "gevonden";
type Dialog =
  | { type: "company"; company?: CompanyDto }
  | { type: "person"; person?: DirectoryPersonDto; companyId?: string }
  | { type: "mail"; target: MailTarget; links: ContactLinkDto[] };

/** Snelfilter vanuit de aandachtsbalk boven de lijst. */
type Attention = "" | "cert" | "notice" | "late";

/** Opzeggen of aflopen binnen zoveel dagen (zelfde termijn als de contractsignalen). */
const NOTICE_DAYS = 60;

/** Contract dat binnenkort opgezegd moet worden of afloopt. */
function needsNotice(k: ContractSummaryDto, today: string) {
  if (k.state !== "lopend") return false;
  const limit = addDays(today, NOTICE_DAYS);
  if (k.autoRenew) return !!k.noticeBy && k.noticeBy >= today && k.noticeBy <= limit;
  return !!k.currentEndOn && k.currentEndOn <= limit;
}

function AttentionCard({
  icon,
  tone,
  title,
  sub,
  pressed,
  onClick,
}: {
  icon: ReactNode;
  tone: "danger" | "warn";
  title: string;
  sub: string;
  pressed: boolean;
  onClick: () => void;
}) {
  return (
    <button type="button" className="co-attn" aria-pressed={pressed} onClick={onClick}>
      <span className={`co-ico co-ico-${tone}`} aria-hidden="true">
        {icon}
      </span>
      <span className="grow">
        <strong>{title}</strong>
        <span className="small muted co-block">{sub}</span>
      </span>
    </button>
  );
}

function CertChip({ state, label }: { state: CertState; label?: string }) {
  return <span className={`chip dir-cert dir-cert-${state}`}>{label ?? CERT_STATE_LABELS[state]}</span>;
}

/**
 * Adresboek (docs/ADRESBOEK.md): bedrijven en personen buiten de eigen gebruikers. Een bedrijf opent als eigen
 * pagina (`/adresboek/bedrijf/:id`); een persoon in het paneel rechts (`?persoon=`, op de telefoon over de lijst).
 * Een oud adres met `?bedrijf=` gaat door naar de bedrijfspagina.
 */
export function DirectoryPage() {
  const { data: me } = useMe();
  const [params, setParams] = useSearchParams();
  const tabRaw = params.get("tab");
  const tab: Tab = tabRaw === "personen" || tabRaw === "gevonden" ? tabRaw : "bedrijven";
  const selCompany = params.get("bedrijf");
  const selPerson = params.get("persoon");
  const [q, setQ] = useState("");
  const [kind, setKind] = useState<CompanyKind | "">("");
  const [archived, setArchived] = useState(false);
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [attn, setAttn] = useState<Attention>("");
  const navigate = useNavigate();
  const canEdit = hasModule(me, "directory", "edit");
  const legacy = useLegacyNames(canEdit);

  const companies = useQuery({
    queryKey: ["companies", archived ? "archived" : "active"],
    queryFn: () => api<CompanyDto[]>(`/companies${archived ? "?archived=1" : ""}`),
    enabled: tab === "bedrijven",
  });
  const stats = useQuery({
    queryKey: ["companies", "stats"],
    queryFn: () => api<CompanyStatsDto[]>("/companies/stats"),
    enabled: tab === "bedrijven" && hasModule(me, "directory"),
  });
  const contracts = useContracts();
  const people = useQuery({
    queryKey: ["people", archived ? "archived" : "active"],
    queryFn: () => api<DirectoryPersonDto[]>(`/people${archived ? "?archived=1" : ""}`),
    enabled: tab === "personen",
  });

  const select = (next: { tab?: Tab; bedrijf?: string | null; persoon?: string | null }) => {
    const p = new URLSearchParams(params);
    if (next.tab) p.set("tab", next.tab);
    for (const k of ["bedrijf", "persoon"] as const) {
      if (next[k] === undefined) continue;
      if (next[k]) p.set(k, next[k]);
      else p.delete(k);
    }
    setParams(p, { replace: true });
  };
  const openCompany = (id: string) => navigate(companyPath(id));
  const openPerson = (id: string) => select({ persoon: id, bedrijf: null });

  if (selCompany) return <Navigate to={companyPath(selCompany)} replace />;
  if (!hasModule(me, "directory"))
    return (
      <div className="center">
        <p>Geen toegang tot de module Adresboek.</p>
      </div>
    );

  const needle = q.trim().toLowerCase();
  const today = todayIso();
  const statOf = new Map((stats.data ?? []).map((s) => [s.companyId, s]));
  const contractsOf = new Map<string, ContractSummaryDto[]>();
  for (const k of contracts.data ?? [])
    if (k.supplier && (k.state === "lopend" || k.state === "nog_niet"))
      contractsOf.set(k.supplier.id, [...(contractsOf.get(k.supplier.id) ?? []), k]);
  const all = companies.data ?? [];
  const certAttn = all.filter((c) => c.certState === "warn" || c.certState === "expired");
  const noticeAttn = all.filter((c) => (contractsOf.get(c.id) ?? []).some((k) => needsNotice(k, today)));
  const lateAttn = all.filter((c) => (statOf.get(c.id)?.lateWork ?? 0) > 0);
  const attnIds = new Set(
    (attn === "cert" ? certAttn : attn === "notice" ? noticeAttn : attn === "late" ? lateAttn : []).map((c) => c.id),
  );
  const names = (xs: CompanyDto[]) =>
    xs
      .slice(0, 3)
      .map((c) => c.name)
      .join(", ") + (xs.length > 3 ? ` en ${xs.length - 3} meer` : "");
  const toggle = (a: Attention) => setAttn(attn === a ? "" : a);
  const companyRows = all.filter(
    (c) =>
      (!attn || attnIds.has(c.id)) &&
      (!kind || c.kinds.includes(kind)) &&
      (!needle || [c.name, c.city, c.trades.join(" "), c.email, c.phone].join(" ").toLowerCase().includes(needle)),
  );
  const seeContracts = hasModule(me, "contracts");
  const personRows = (people.data ?? []).filter(
    (p) =>
      !needle ||
      [p.name, p.jobTitle, p.company?.name, p.email, p.phone, p.mobile].join(" ").toLowerCase().includes(needle),
  );
  const hasSel = !!selPerson;

  return (
    <div className={`page dir-page ${hasSel ? "has-sel" : ""}`}>
      <PageHeader
        label="Alle gebouwen"
        title="Adresboek"
        menu={
          <a className="menuitem" href="/api/directory/export.xlsx" download>
            <IconDownload size={20} aria-hidden="true" />
            Excel
          </a>
        }
        action={
          canEdit &&
          tab !== "gevonden" && (
            <button
              type="button"
              className="btn primary"
              onClick={() => setDialog(tab === "personen" ? { type: "person" } : { type: "company" })}
            >
              <IconPlus aria-hidden="true" /> {tab === "personen" ? "Persoon" : "Bedrijf"}
            </button>
          )
        }
        tabs={
          <Tabs
            label="Onderdeel"
            items={[
              { value: "bedrijven", label: "Bedrijven" },
              { value: "personen", label: "Personen" },
              ...(canEdit && legacy.data?.length
                ? [{ value: "gevonden" as const, label: "Gevonden", count: legacy.data.length }]
                : []),
            ]}
            value={tab}
            onChange={(v) => select({ tab: v })}
          />
        }
      />
      <p className="muted small">
        Leveranciers, huurders en andere contacten bij je gebouwen. Contactpersonen van klanten staan bij Organisatie ›
        Klanten.
      </p>
      {tab === "gevonden" ? (
        <LegacyNames onOpenCompany={openCompany} />
      ) : (
        <>
          {tab === "bedrijven" && (certAttn.length > 0 || noticeAttn.length > 0 || lateAttn.length > 0) && (
            <div className="co-attns" role="group" aria-label="Aandacht nodig">
              {certAttn.length > 0 && (
                <AttentionCard
                  icon={<IconShield size={18} />}
                  tone="danger"
                  title={
                    certAttn.length === 1
                      ? "1 erkenning verloopt of is verlopen"
                      : `${certAttn.length} erkenningen verlopen (bijna)`
                  }
                  sub={names(certAttn)}
                  pressed={attn === "cert"}
                  onClick={() => toggle("cert")}
                />
              )}
              {noticeAttn.length > 0 && (
                <AttentionCard
                  icon={<IconContract size={18} />}
                  tone="warn"
                  title={
                    noticeAttn.length === 1
                      ? "1 leverancier: contract opzeggen of verlengen"
                      : `${noticeAttn.length} leveranciers: contract opzeggen of verlengen`
                  }
                  sub={`Binnen ${NOTICE_DAYS} dagen · ${names(noticeAttn)}`}
                  pressed={attn === "notice"}
                  onClick={() => toggle("notice")}
                />
              )}
              {lateAttn.length > 0 && (
                <AttentionCard
                  icon={<IconAlert size={18} />}
                  tone="danger"
                  title={`${lateAttn.reduce((n, c) => n + (statOf.get(c.id)?.lateWork ?? 0), 0)} keer werk te laat`}
                  sub={names(lateAttn)}
                  pressed={attn === "late"}
                  onClick={() => toggle("late")}
                />
              )}
            </div>
          )}
          <div className="dir-filters">
            <input
              type="search"
              className="dir-search"
              aria-label="Zoeken in het adresboek"
              placeholder={
                tab === "personen" ? "Zoeken op naam, bedrijf, telefoon" : "Zoeken op naam, vakgebied, plaats"
              }
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            {tab === "bedrijven" && (
              <div className="dir-kinds" role="group" aria-label="Soort">
                {(["", "supplier", "tenant", "other"] as const).map((k) => (
                  <button
                    key={k || "all"}
                    type="button"
                    className="kpi"
                    aria-pressed={kind === k}
                    onClick={() => setKind(k)}
                  >
                    {k ? COMPANY_KIND_LABELS[k] : "Alles"}
                  </button>
                ))}
              </div>
            )}
            <label className="small">
              <input type="checkbox" checked={archived} onChange={(e) => setArchived(e.target.checked)} /> Ook
              gearchiveerd
            </label>
          </div>

          <div className="dir-layout">
            <div className="dir-list tscroll">
              {tab === "bedrijven" ? (
                <table className="it it-list" aria-label="Bedrijven" aria-busy={companies.isPending}>
                  <thead>
                    <tr>
                      <th scope="col">Bedrijf</th>
                      <th scope="col" className="dir-hide-narrow">
                        Soort
                      </th>
                      <th scope="col" className="num dir-hide-narrow">
                        Gebouwen
                      </th>
                      {seeContracts && (
                        <th scope="col" className="num dir-hide-narrow">
                          Contracten
                        </th>
                      )}
                      <th scope="col" className="num">
                        Open werk
                      </th>
                      <th scope="col">Erkenning</th>
                    </tr>
                  </thead>
                  <tbody>
                    {companyRows.map((c) => {
                      const st = statOf.get(c.id);
                      const ks = contractsOf.get(c.id) ?? [];
                      const year = ks.reduce((n, k) => n + (k.yearCents ?? 0), 0);
                      return (
                        <tr key={c.id} className="co-click" onClick={() => openCompany(c.id)}>
                          <td className="tcell-title">
                            <button type="button" className="linkbtn" onClick={() => openCompany(c.id)}>
                              {c.name}
                            </button>
                            <div className="muted small">
                              {[c.trades.join(", "), c.city].filter(Boolean).join(" · ")}
                              {c.archivedAt && " · gearchiveerd"}
                            </div>
                          </td>
                          <td className="dir-hide-narrow">
                            <KindChips c={c} />
                          </td>
                          <td className="num dir-hide-narrow">{st?.buildings || <span className="muted">–</span>}</td>
                          {seeContracts && (
                            <td className="num dir-hide-narrow">
                              {ks.length ? (
                                <>
                                  {ks.length}
                                  {year > 0 && (
                                    <div className="muted small">
                                      {(year / 100).toLocaleString("nl-NL", {
                                        style: "currency",
                                        currency: "EUR",
                                        maximumFractionDigits: 0,
                                      })}{" "}
                                      /jr
                                    </div>
                                  )}
                                </>
                              ) : (
                                <span className="muted">–</span>
                              )}
                            </td>
                          )}
                          <td className="num" data-label="open">
                            {st?.openWork ? (
                              <>
                                {st.openWork}
                                {st.lateWork > 0 && <div className="small co-late">{st.lateWork} te laat</div>}
                              </>
                            ) : (
                              <span className="muted">–</span>
                            )}
                          </td>
                          <td>{c.certState ? <CertChip state={c.certState} /> : <span className="muted">–</span>}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <table className="it" aria-label="Personen" aria-busy={people.isPending}>
                  <thead>
                    <tr>
                      <th scope="col">Naam</th>
                      <th scope="col">Bedrijf</th>
                      <th scope="col" className="dir-hide-narrow">
                        Telefoon
                      </th>
                      <th scope="col">Account</th>
                    </tr>
                  </thead>
                  <tbody>
                    {personRows.map((p) => (
                      <tr key={p.id} aria-current={selPerson === p.id} onClick={() => openPerson(p.id)}>
                        <td className="tcell-title">
                          <button type="button" className="linkbtn" onClick={() => openPerson(p.id)}>
                            {p.name}
                          </button>
                          {p.jobTitle && <div className="muted small">{p.jobTitle}</div>}
                        </td>
                        <td>{p.company?.name ?? <span className="muted">Zonder bedrijf</span>}</td>
                        <td className="dir-hide-narrow num">{p.mobile ?? p.phone ?? ""}</td>
                        <td>
                          {p.user ? (
                            <span className="chip dir-cert dir-cert-ok">Gebruiker</span>
                          ) : p.userSuggestion ? (
                            <span className="chip dir-cert dir-cert-warn">Voorstel</span>
                          ) : (
                            <span className="muted">–</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {tab === "bedrijven" && companies.data && !companyRows.length && (
                <EmptyState
                  icon={IconSearch}
                  title="Geen bedrijven gevonden"
                  text="Pas de zoekterm of het filter aan."
                />
              )}
              {tab === "personen" && people.data && !personRows.length && (
                <p className="muted">{needle ? "Geen personen gevonden." : "Nog geen personen in het adresboek."}</p>
              )}
            </div>
            {selPerson && (
              <PersonPanel
                key={selPerson}
                id={selPerson}
                canEdit={canEdit}
                onClose={() => select({ persoon: null })}
                onOpenCompany={openCompany}
                onDialog={setDialog}
              />
            )}
          </div>
        </>
      )}

      {dialog?.type === "company" && (
        <CompanyDialog
          company={dialog.company}
          onClose={() => setDialog(null)}
          onSaved={(id) => {
            setDialog(null);
            openCompany(id);
          }}
        />
      )}
      {dialog?.type === "person" && (
        <PersonDialog
          person={dialog.person}
          companyId={dialog.companyId}
          onClose={() => setDialog(null)}
          onSaved={(id) => {
            setDialog(null);
            openPerson(id);
          }}
        />
      )}
      {dialog?.type === "mail" && (
        <MailDialog target={dialog.target} links={dialog.links} onClose={() => setDialog(null)} />
      )}
    </div>
  );
}

function PanelHead({
  title,
  sub,
  onClose,
  onEdit,
}: {
  title: string;
  sub?: React.ReactNode;
  onClose: () => void;
  onEdit?: () => void;
}) {
  return (
    <header className="dir-panelhead">
      <div className="grow">
        <h2>{title}</h2>
        {sub && <div className="dir-sub">{sub}</div>}
      </div>
      {onEdit && (
        <button type="button" className="btn small ghost" onClick={onEdit} aria-label={`${title} bewerken`}>
          <IconPencil aria-hidden="true" /> Bewerken
        </button>
      )}
      <button type="button" className="btn ghost small ico" onClick={onClose} aria-label="Paneel sluiten">
        <IconClose aria-hidden="true" />
      </button>
    </header>
  );
}

function PersonPanel({
  id,
  canEdit,
  onClose,
  onOpenCompany,
  onDialog,
}: {
  id: string;
  canEdit: boolean;
  onClose: () => void;
  onOpenCompany: (id: string) => void;
  onDialog: (d: Dialog) => void;
}) {
  const toast = useToast();
  const refresh = useDirectoryRefresh();
  const q = useQuery({ queryKey: ["person", id], queryFn: () => api<DirectoryPersonDto>(`/people/${id}`) });
  const [confirmErase, setConfirmErase] = useState(false);
  const p = q.data;
  if (!p)
    return (
      <aside className="dir-panel" aria-label="Persoon" aria-busy="true">
        {q.isError ? <p className="muted">{errMsg(q.error)}</p> : <Loading />}
      </aside>
    );
  const act = async (path: string, ok: string, body?: object) => {
    try {
      await api(`/people/${p.id}/${path}`, { method: "POST", body });
      await refresh();
      toast(ok);
    } catch (err) {
      toast(errMsg(err));
    }
  };
  return (
    <aside className="dir-panel" aria-label={p.name}>
      <PanelHead
        title={p.name}
        sub={
          <>
            {p.jobTitle}
            {p.jobTitle && p.company && " · "}
            {p.company && (
              <button type="button" className="linkbtn" onClick={() => onOpenCompany(p.company!.id)}>
                {p.company.name}
              </button>
            )}
          </>
        }
        onClose={onClose}
        onEdit={canEdit && !p.erased ? () => onDialog({ type: "person", person: p }) : undefined}
      />
      {canEdit && p.userSuggestion && (
        <section className="dir-sec">
          <div className="dir-suggest" role="status">
            <p>
              <strong>Is dit dezelfde als gebruiker {p.userSuggestion.name ?? p.userSuggestion.email}?</strong>
              <br />
              <span className="small muted">
                Zelfde mailadres. Na koppelen ziet deze persoon zijn afspraken in Mijn taken.
              </span>
            </p>
            <div className="dir-actions">
              <button
                type="button"
                className="btn small primary"
                onClick={() => void act("link-user", "Gekoppeld aan de gebruiker", { userId: p.userSuggestion!.id })}
              >
                Koppelen
              </button>
              <button
                type="button"
                className="btn small"
                onClick={() => void act("dismiss-user-suggestion", "Voorstel weggeklikt")}
              >
                Niet dezelfde
              </button>
            </div>
          </div>
        </section>
      )}
      <section className="dir-sec">
        <h3>Gegevens</h3>
        {p.erased ? (
          <p className="muted small">De persoonsgegevens zijn gewist.</p>
        ) : (
          <dl className="kv">
            {p.mobile && (
              <>
                <dt>Mobiel</dt>
                <dd>
                  <Phone n={p.mobile} />
                </dd>
              </>
            )}
            {p.phone && (
              <>
                <dt>Telefoon</dt>
                <dd>
                  <Phone n={p.phone} />
                </dd>
              </>
            )}
            {p.email && (
              <>
                <dt>E-mail</dt>
                <dd className="dir-wrap">
                  <a href={`mailto:${p.email}`}>{p.email}</a>
                </dd>
              </>
            )}
            {p.availability && (
              <>
                <dt>Bereikbaar</dt>
                <dd>{p.availability}</dd>
              </>
            )}
            <dt>Account</dt>
            <dd>
              {p.user ? (
                <>
                  {p.user.name ?? p.user.email}{" "}
                  {canEdit && (
                    <button
                      type="button"
                      className="linkbtn small"
                      onClick={() => void act("unlink-user", "Ontkoppeld")}
                    >
                      ontkoppelen
                    </button>
                  )}
                </>
              ) : (
                "Geen"
              )}
            </dd>
          </dl>
        )}
        {p.notes && <p className="small dir-notes">{p.notes}</p>}
      </section>
      {!p.erased && <LinksSection links={p.links} />}
      {!p.erased && (
        <Logbook
          entity="person"
          id={p.id}
          path={`/people/${p.id}/log`}
          canComment={canEdit}
          className="dir-sec logbook"
          actions={
            canEdit &&
            !p.archivedAt &&
            !!p.email && (
              <button
                type="button"
                className="btn small"
                onClick={() => onDialog({ type: "mail", target: { personId: p.id }, links: p.links ?? [] })}
              >
                <IconMail aria-hidden="true" /> Mail sturen
              </button>
            )
          }
        />
      )}
      {canEdit && !p.erased && (
        <section className="dir-sec dir-actions">
          <button
            type="button"
            className="btn small"
            onClick={() =>
              void act(p.archivedAt ? "unarchive" : "archive", p.archivedAt ? "Teruggezet" : "Gearchiveerd")
            }
          >
            {p.archivedAt ? "Terugzetten" : "Archiveren"}
          </button>
          {confirmErase ? (
            <>
              <span className="small">Naam en contactgegevens wissen? Dit kan niet terug.</span>
              <button
                type="button"
                className="btn small danger"
                onClick={() => void act("erase", "Persoonsgegevens gewist").then(() => setConfirmErase(false))}
              >
                Ja, wissen
              </button>
              <button type="button" className="btn small ghost" onClick={() => setConfirmErase(false)}>
                Annuleren
              </button>
            </>
          ) : (
            <button type="button" className="btn small ghost" onClick={() => setConfirmErase(true)}>
              Persoonsgegevens wissen
            </button>
          )}
        </section>
      )}
    </aside>
  );
}
